/**
 * 现场会面详情：查看一次会面的时间、对方单位、参加人员，并提供编辑 / 完成 / 取消。
 *
 * 需求背景：周历上的会面卡点击后需要能看到完整信息（对方联系人电话、参加人员、
 * 会面要点）并可改期、标记完成或取消。会面与市检详情共用同一套打开方式
 * （周历格子里点开弹窗），但会面没有检查结果、整改、复检这些市检专有区块。
 *
 * 权限口径：canEdit 由服务端按管理权限下发；没有编辑权限的参与者仍能读到详情
 * （知道自己要去哪、见谁），但看不到编辑与取消按钮。
 */
import React, { useEffect, useRef, useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../ui/dialog";
import { Button } from "../ui/button";
import { MapPin, Users } from "lucide-react";
import SiteMeetingForm from "./SiteMeetingForm";
import { meetingExternalLabel, meetingKindLabel, meetingStatusLabel, meetingWhenLabel } from "./SiteMeetingCard";

const input = "mt-1 block w-full min-w-0 rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm disabled:bg-slate-100";
const failure = (e) => typeof e?.response?.data?.detail === "string" ? e.response.data.detail : e.message;
const ACTIONS = {
  created: ["会面已安排", "Meeting scheduled"], updated: ["会面信息更新", "Meeting updated"],
  done: ["会面已完成", "Meeting completed"], cancelled: ["会面已取消", "Meeting cancelled"],
  reopened: ["会面已恢复", "Meeting reopened"],
};

export default function SiteMeetingDetail({ id, api, zh, onClose, onUpdated = () => {} }) {
  const [row, setRow] = useState(null), [error, setError] = useState(""), [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false), [edit, setEdit] = useState(false), [action, setAction] = useState("");
  const [form, setForm] = useState({ reason: "" }), [retry, setRetry] = useState(0);
  const submitting = useRef(false);

  useEffect(() => {
    const ctl = new AbortController();
    setLoading(true); setError(""); setRow(null); setAction(""); setEdit(false);
    api.get(`/site-meetings/${id}`, { signal: ctl.signal })
      .then(({ data }) => { if (!ctl.signal.aborted) setRow(data); })
      .catch((e) => { if (!ctl.signal.aborted) setError(failure(e)); })
      .finally(() => { if (!ctl.signal.aborted) setLoading(false); });
    return () => ctl.abort();
  }, [api, id, retry]);

  const updated = (next) => {
    setRow(next); setEdit(false); setAction(""); setError("");
    onUpdated(next);
    // 与市检共用刷新事件：周历与项目顶部摘要都监听该事件，保持整站日程一致。
    window.dispatchEvent(new Event("sub-schedule-changed"));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (submitting.current) return;
    submitting.current = true; setBusy(true); setError("");
    try {
      const { data } = await api.patch(`/site-meetings/${id}`, { ...form, action, version: row.version });
      updated(data);
    } catch (e) { setError(failure(e)); }
    finally { submitting.current = false; setBusy(false); }
  };

  if (edit) return <SiteMeetingForm record={row} projectId={row.projectId}
    projects={[{ id: row.projectId, address: row.address }]} api={api} zh={zh}
    onClose={() => setEdit(false)} onSaved={updated} />;

  const cancelled = row?.status === "cancelled";
  const done = row?.status === "done";

  return <Dialog open onOpenChange={(open) => { if (!open && !busy) onClose(); }}>
    <DialogContent className="max-h-[90dvh] overflow-y-auto sm:max-w-2xl" data-testid="site-meeting-detail" closeLabel={zh ? "关闭会面详情" : "Close meeting details"} closeDisabled={busy}>
      <DialogHeader>
        <DialogTitle>{row?.title || (zh ? "现场会面" : "Site meeting")}</DialogTitle>
        <DialogDescription>{row?.address || (zh ? "会面时间与参加人员" : "Meeting time and attendees")}</DialogDescription>
      </DialogHeader>
      {loading && <p role="status" className="text-sm text-slate-500">{zh ? "正在读取会面安排…" : "Loading meeting…"}</p>}
      {error && <div role="alert" className="rounded-lg bg-red-50 p-3 text-sm text-red-700">{error}
        <button type="button" disabled={busy} onClick={() => setRetry((n) => n + 1)} className="ml-2 underline">{zh ? "重新读取" : "Reload"}</button></div>}
      {row && <div className="space-y-4 text-sm">
        <div className="flex flex-wrap gap-2">
          <span className={`rounded-full px-3 py-1 text-xs font-bold text-white ${cancelled ? "bg-slate-500" : done ? "bg-emerald-600" : "bg-slate-700"}`} data-testid="site-meeting-status">{meetingStatusLabel(row.status, zh)}</span>
          <span className="rounded-full bg-slate-100 px-3 py-1 text-xs text-slate-700">{meetingKindLabel(row.kind, zh)}</span>
          <span className="flex items-center gap-1 rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold tabular-nums text-slate-700">{meetingWhenLabel(row, zh)}</span>
        </div>
        <div className="grid gap-3 rounded-xl border border-slate-200 bg-slate-50/60 p-4 sm:grid-cols-2">
          <div><p className="text-xs text-slate-500">{zh ? "会面日期" : "Meeting date"}</p><p className="mt-1 font-semibold" data-testid="site-meeting-date-value">{row.date}</p></div>
          <div><p className="text-xs text-slate-500">{zh ? "会面时间" : "Meeting time"}</p><p className="mt-1 font-semibold" data-testid="site-meeting-time-value">{meetingWhenLabel(row, zh)}</p></div>
          <div><p className="text-xs text-slate-500">{zh ? "外部单位" : "External party"}</p><p className="mt-1 font-semibold">{meetingExternalLabel(row, zh)}</p>
            {(row.externalContact || row.externalPhone) && <p className="mt-0.5 text-xs text-slate-600">{[row.externalContact, row.externalPhone].filter(Boolean).join(" · ")}</p>}</div>
          <div><p className="text-xs text-slate-500">{zh ? "会面负责人" : "Meeting owner"}</p><p className="mt-1 font-semibold">{row.ownerName}</p></div>
        </div>
        <p className="flex items-start gap-1.5 break-words text-slate-700"><MapPin aria-hidden="true" className="mt-0.5 h-4 w-4 shrink-0 text-slate-400" />{row.location || row.address || "—"}</p>
        <p className="flex items-start gap-1.5 break-words text-slate-700"><Users aria-hidden="true" className="mt-0.5 h-4 w-4 shrink-0 text-slate-400" />
          <span><span className="text-slate-500">{zh ? "现场参加：" : "On-site: "}</span>{(row.attendees || []).map((p) => p.name).join("、") || "—"}</span></p>
        {row.notes && <p className="whitespace-pre-wrap break-words rounded-lg bg-slate-50 p-3"><span className="font-medium">{zh ? "会面要点：" : "Agenda: "}</span>{row.notes}</p>}
        {row.cancelReason && <p className="rounded-lg bg-slate-100 p-3">{zh ? "取消原因：" : "Cancellation reason: "}{row.cancelReason}</p>}

        {!action && <div className="flex flex-wrap justify-end gap-2">
          {row.canEdit && <Button variant="outline" disabled={busy} data-testid="site-meeting-edit" onClick={() => setEdit(true)}>{zh ? "编辑会面" : "Edit meeting"}</Button>}
          {row.canEdit && !cancelled && !done && <Button disabled={busy} data-testid="site-meeting-done" onClick={() => { setForm({ reason: "" }); setAction("done"); }}>{zh ? "标记已完成" : "Mark completed"}</Button>}
          {row.canEdit && !cancelled && <Button variant="outline" disabled={busy} className="text-red-700" data-testid="site-meeting-cancel" onClick={() => { setForm({ reason: "" }); setAction("cancel"); }}>{zh ? "取消会面" : "Cancel meeting"}</Button>}
          {/* 误点完成或取消后可恢复；状态不会静默改动，恢复也会写进变更记录。 */}
          {row.canEdit && (cancelled || done) && <Button variant="outline" disabled={busy} data-testid="site-meeting-reopen" onClick={() => { setForm({ reason: "" }); setAction("reopen"); }}>{zh ? "恢复为已安排" : "Reopen"}</Button>}
        </div>}

        {action && <form onSubmit={submit} data-testid="site-meeting-action-form" className="space-y-3 rounded-xl border border-slate-200 bg-slate-50/60 p-4">
          <fieldset disabled={busy} className="min-w-0 space-y-3">
            {action === "cancel" && <label className="block text-xs font-medium">{zh ? "取消原因 *（记录将留档）" : "Cancellation reason * (record retained)"}
              <textarea required className={input} rows={3} maxLength={2000} data-testid="site-meeting-cancel-reason" value={form.reason} onChange={(e) => setForm({ reason: e.target.value })} /></label>}
            {action === "done" && <p className="text-sm text-slate-700">{zh ? "确认这次现场会面已经完成？完成后仍可恢复为已安排。" : "Confirm this on-site meeting took place? You can reopen it later."}</p>}
            {action === "reopen" && <p className="text-sm text-slate-700">{zh ? "把这次会面恢复为「已安排」？" : "Move this meeting back to Scheduled?"}</p>}
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => setAction("")}>{zh ? "返回" : "Back"}</Button>
              <Button type="submit" data-testid="site-meeting-action-save">{busy ? (zh ? "保存中…" : "Saving…") : (zh ? "确认" : "Confirm")}</Button>
            </div>
          </fieldset>
        </form>}

        {!!(row.history || []).length && <details className="border-t border-slate-200 pt-3">
          <summary className="cursor-pointer text-xs font-semibold text-slate-600">{zh ? "会面变更记录" : "Meeting history"} ({row.history.length})</summary>
          <ol className="mt-3 space-y-3">{[...row.history].reverse().map((h, i) => <li key={`${h.at}-${i}`} className="border-l-2 border-slate-200 pl-3 text-xs">
            <p className="font-semibold">{ACTIONS[h.action]?.[zh ? 0 : 1] || h.action}</p>
            <p className="mt-1 text-slate-500">{h.by} · {new Date(h.at).toLocaleString(zh ? "zh-CN" : "en-US", { timeZone: "America/Los_Angeles" })}</p>
            {h.date && <p>{h.previousDate && h.previousDate !== h.date ? `${h.previousDate} → ${h.date}` : h.date}{h.startTime ? ` · ${h.startTime}` : ""}</p>}
            {(h.notes || h.cancelReason) && <p className="mt-1 whitespace-pre-wrap break-words">{h.notes || h.cancelReason}</p>}
          </li>)}</ol>
        </details>}
      </div>}
      <div className="flex justify-end"><Button variant="outline" disabled={busy} onClick={onClose} data-testid="site-meeting-close">{zh ? "关闭" : "Close"}</Button></div>
    </DialogContent>
  </Dialog>;
}
